import React, { createContext, useContext, useMemo } from 'react';
import { AuthProvider as OidcAuthProvider, useAuth as useOidcAuth } from 'react-oidc-context';
import { User, WebStorageStateStore } from 'oidc-client-ts';

// Configuration from environment variables
const OIDC_AUTHORITY = process.env.REACT_APP_OIDC_AUTHORITY || '';
const OIDC_CLIENT_ID = process.env.REACT_APP_OIDC_CLIENT_ID || '';
const ROLE_ADMIN_GROUP = process.env.REACT_APP_ROLE_ADMIN_GROUP || 'pdf-schubse-admins';
const ROLE_EDITOR_GROUP = process.env.REACT_APP_ROLE_EDITOR_GROUP || 'pdf-schubse-editors';

const oidcConfig = {
  authority: OIDC_AUTHORITY,
  client_id: OIDC_CLIENT_ID,
  redirect_uri: window.location.origin + '/',
  post_logout_redirect_uri: window.location.origin + '/',
  scope: 'openid profile email groups',
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: true,
  onSigninCallback: () => {
    // Remove OIDC callback parameters from URL after successful login
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

// Role types matching backend
export type Role = 'admin' | 'editor' | 'viewer';

interface AuthContextType {
  user: User | null | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
  accessToken: string | undefined;
  login: () => void;
  logout: () => void;
  error: Error | undefined;
  // Role-based access
  role: Role;
  groups: string[];
  isAdmin: boolean;
  isEditor: boolean;
  isViewer: boolean;
  canEdit: boolean; // admin or editor
  canDelete: boolean; // admin only
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Determine role from groups using configured group names
const determineRole = (groups: string[]): Role => {
  const adminGroup = ROLE_ADMIN_GROUP.toLowerCase();
  const editorGroup = ROLE_EDITOR_GROUP.toLowerCase();

  for (const group of groups) {
    const groupLower = group.toLowerCase();
    if (groupLower === adminGroup) {
      return 'admin';
    }
    if (groupLower === editorGroup) {
      return 'editor';
    }
  }
  return 'viewer';
};

// Extract groups from user profile
const extractGroups = (user: User | null | undefined): string[] => {
  if (!user?.profile) return [];
  const groups = (user.profile as any).groups;
  if (Array.isArray(groups)) {
    return groups;
  }
  return [];
};

const AuthContextWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useOidcAuth();

  const contextValue = useMemo<AuthContextType>(() => {
    const groups = extractGroups(auth.user);
    const role = determineRole(groups);
    const isAdmin = role === 'admin';
    const isEditor = role === 'editor';
    const isViewer = role === 'viewer';

    return {
      user: auth.user,
      isLoading: auth.isLoading,
      isAuthenticated: auth.isAuthenticated,
      accessToken: auth.user?.access_token,
      login: () => auth.signinRedirect(),
      logout: () => auth.signoutRedirect(),
      error: auth.error,
      // Role-based access
      role,
      groups,
      isAdmin,
      isEditor,
      isViewer,
      canEdit: isAdmin || isEditor,
      canDelete: isAdmin,
    };
  }, [auth.user, auth.isLoading, auth.isAuthenticated, auth.error]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <OidcAuthProvider {...oidcConfig}>
      <AuthContextWrapper>
        {children}
      </AuthContextWrapper>
    </OidcAuthProvider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
